/**
 * UI state — panel visibility, interaction tracking, dialogs, toasts.
 */

import { makeAutoObservable } from 'mobx';
import type { BibleLocation } from '@skjv/common';
import type { UserDataTab, ConfirmDialogConfig, ToastConfig } from './ui-types';
import { isDesktop } from '@/desktop/desktop';

export type { UserDataTab, ConfirmDialogConfig, ToastConfig };

/** Popover prompt anchored to a clicked pericope heading in the reader. */
export interface PericopeStudyPrompt {
  location: BibleLocation;
  title: string;
  anchorRect: { top: number; left: number; bottom: number; width: number };
}

export class UiState {
  interactionLocation: BibleLocation | null = null;
  verseMenuVisible = false;
  noteEditorVisible = false;
  bookmarkCreatorVisible = false;
  userDataPanelVisible = false;
  chatPanelVisible = false;
  chatFullscreen = false;
  // Web keeps only search/problems/history (DESKTOP-PRD §3.8) — its default
  // must be a surviving tab; desktop keeps the classic bookmarks landing.
  userDataPanelTab: UserDataTab = isDesktop() ? 'bookmarks' : 'search';
  confirmDialog: ConfirmDialogConfig | null = null;
  toasts: ToastConfig[] = [];
  pericopeStudyPrompt: PericopeStudyPrompt | null = null;

  private toastIdCounter = 0;

  constructor() {
    makeAutoObservable(this);
  }

  setInteractionLocation(location: BibleLocation | null): void {
    this.interactionLocation = location;
  }

  showVerseMenu(): void { this.verseMenuVisible = true; }
  hideVerseMenu(): void { this.verseMenuVisible = false; }

  showPericopeStudyPrompt(location: BibleLocation, title: string, anchorRect: DOMRect): void {
    this.pericopeStudyPrompt = {
      location,
      title,
      anchorRect: {
        top: anchorRect.top,
        left: anchorRect.left,
        bottom: anchorRect.bottom,
        width: anchorRect.width,
      },
    };
  }

  hidePericopeStudyPrompt(): void { this.pericopeStudyPrompt = null; }

  showNoteEditor(): void {
    this.verseMenuVisible = false;
    this.noteEditorVisible = true;
  }

  hideNoteEditor(): void { this.noteEditorVisible = false; }

  showUserDataPanel(tab: UserDataTab): void {
    this.userDataPanelTab = tab;
    this.userDataPanelVisible = true;
  }

  hideUserDataPanel(): void { this.userDataPanelVisible = false; }
  setUserDataPanelTab(tab: UserDataTab): void { this.userDataPanelTab = tab; }

  showChatPanel(): void { this.chatPanelVisible = true; }

  hideChatPanel(): void {
    this.chatPanelVisible = false;
    this.chatFullscreen = false;
  }

  toggleChatFullscreen(): void { this.chatFullscreen = !this.chatFullscreen; }

  showBookmarkCreator(): void {
    this.verseMenuVisible = false;
    this.bookmarkCreatorVisible = true;
  }

  hideBookmarkCreator(): void { this.bookmarkCreatorVisible = false; }

  hideAllPanels(): void {
    this.verseMenuVisible = false;
    this.noteEditorVisible = false;
    this.userDataPanelVisible = false;
    this.chatPanelVisible = false;
    this.bookmarkCreatorVisible = false;
  }

  // ── Confirm Dialog ──────────────────────────────────────────

  showConfirmDialog(config: Omit<ConfirmDialogConfig, 'id'>): void {
    this.confirmDialog = config;
  }

  hideConfirmDialog(): void { this.confirmDialog = null; }

  // ── Toasts ──────────────────────────────────────────────────

  showToast(message: string, variant?: ToastConfig['variant'], duration?: number): void {
    const id = `toast-${++this.toastIdCounter}`;
    this.toasts.push({ id, message, variant, duration });
  }

  removeToast(id: string): void {
    this.toasts = this.toasts.filter((t) => t.id !== id);
  }
}
